/**
 * The device-storage port.
 *
 * Five modules in the mobile portal were this same thing written five times —
 * the session token, the runtime token, the selected sale, the theme and the
 * locale. Each one was an async get/set/clear over a versioned `aac.*.vN` key,
 * each one caught its own errors, and each one imported Capacitor Preferences
 * directly. That last part is why none of them could move: a store that imports
 * `@capacitor/preferences` cannot run on a desktop, and it cannot run in a test
 * either.
 *
 * So the SDK defines the interface and the host supplies the driver. Mobile
 * supplies Capacitor Preferences, a desktop app supplies `localStorage`, and a
 * test supplies a Map (see createMemoryStorageDriver).
 *
 * THE RULE, and it is the whole reason this file has any code in it rather than
 * being a comment: **every method resolves, never rejects.** Storage is read on
 * the boot path — session hydration runs before the app mounts — so a rejection
 * there is a blank screen rather than a trip to the login page. That is not
 * hypothetical: it is exactly what a native build missing the Preferences
 * plugin used to do. A driver is allowed to throw; this wrapper is what makes
 * sure the store above it never sees it.
 */

/**
 * A driver is the smallest thing a host has to write:
 *
 *   get({ key })            -> { value: string | null } | string | null
 *   set({ key, value })     -> anything
 *   remove({ key })         -> anything
 *
 * Capacitor's `Preferences` satisfies it as-is, which is why the shape is what
 * it is rather than something tidier. Any of the three may throw or reject;
 * none of them may be relied on to.
 */

const isFunction = (candidate) => typeof candidate === 'function';

/**
 * Read the string a driver's `get` answered with, whichever of the two shapes
 * it used. Capacitor answers `{ value }`; a hand-rolled localStorage driver
 * usually answers the string itself, and both are reasonable enough that
 * accepting one and silently failing the other would be a trap.
 */
const valueOf = (answer) => {
    if (answer == null) return null;
    if (typeof answer === 'string') return answer;
    return typeof answer.value === 'string' ? answer.value : null;
};

/**
 * Wrap a driver in the port's guarantees.
 *
 * `name` appears in the warning when a call fails, so a silent degradation
 * still leaves a trail — the failure mode this port exists to produce is a
 * missing value, and a missing value with no explanation is the hardest kind
 * of bug to find.
 */
export function createDeviceStorage(driver, { name = 'deviceStorage', onWarn = console.warn } = {}) {
    const warn = (operation, error) => {
        if (isFunction(onWarn)) onWarn(`${name}.${operation} failed`, error);
    };

    const get = async (key) => {
        if (!driver || !isFunction(driver.get)) return null;
        try {
            return valueOf(await driver.get({ key }));
        } catch (error) {
            warn('get', error);
            return null;
        }
    };

    const set = async (key, value) => {
        if (!driver || !isFunction(driver.set)) return;
        try {
            await driver.set({ key, value: String(value) });
        } catch (error) {
            warn('set', error);
        }
    };

    const remove = async (key) => {
        if (!driver || !isFunction(driver.remove)) return;
        try {
            await driver.remove({ key });
        } catch (error) {
            warn('remove', error);
        }
    };

    /**
     * The JSON pair, for the values that are a shape rather than a string.
     *
     * Malformed stored JSON resolves to `null` like a missing value does. A
     * half-written or hand-edited value is indistinguishable from an absent one
     * as far as anything upstream can act on, and throwing here would defeat
     * the point of the port.
     */
    const getJson = async (key) => {
        const raw = await get(key);
        if (raw == null) return null;
        try {
            return JSON.parse(raw);
        } catch (error) {
            warn('getJson', error);
            return null;
        }
    };

    const setJson = async (key, value) => {
        let encoded;
        try {
            encoded = JSON.stringify(value);
        } catch (error) {
            // A cycle or a BigInt — a programming error rather than an
            // environmental one, but still not worth a blank screen.
            warn('setJson', error);
            return;
        }
        await set(key, encoded);
    };

    return { get, set, remove, getJson, setJson };
}

/**
 * A driver backed by a Map — what a test supplies, and what the SDK falls back
 * to when a host has configured no storage at all.
 *
 * The fallback matters: without it, a store that persists something would have
 * to check whether storage exists before every call, and the check would be
 * wrong somewhere. With it, an unconfigured SDK forgets everything between
 * reloads and works perfectly otherwise, which is the right failure.
 */
export function createMemoryStorageDriver(initial = {}) {
    const entries = new Map(Object.entries(initial));
    return {
        get: ({ key }) => ({ value: entries.has(key) ? entries.get(key) : null }),
        set: ({ key, value }) => { entries.set(key, value); },
        remove: ({ key }) => { entries.delete(key); },
        // Not part of the port — a test's own affordance for asserting on what
        // was written without going back through the wrapper.
        entries
    };
}

/**
 * A driver backed by the Web Storage API — what a desktop app supplies.
 *
 * Every call is guarded because `localStorage` is not always there to be used:
 * a private window can refuse it, a browser can be configured to block site
 * data, and the accessor itself throws in some of those cases rather than
 * returning null.
 */
export function createWebStorageDriver(storage = globalThis.localStorage) {
    return {
        get: ({ key }) => ({ value: storage?.getItem(key) ?? null }),
        set: ({ key, value }) => { storage?.setItem(key, value); },
        remove: ({ key }) => { storage?.removeItem(key); }
    };
}
